import { useCallback, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { parseColumnFilters, serializeColumnFilters, type ColumnFilter } from "@/components/clicks/clicksGridEngine";
import { MAX_FILTER_VALUES, addValueToFilter, excludeValues, fieldFilterOf, filterByValue, isTextMode, modeWord } from "@/lib/filter-model";
import { parseUrlActiveFilters, serializeUrlActiveFilters, type UrlActiveFilters } from "@/lib/url-active-filters";

type ColumnFilters = Record<string, ColumnFilter>;

/**
 * The last write still on its way. The router commits a URL only after the
 * route's loaders, and setSearchParams starts from the committed one, so a
 * second write in the same gesture - a chip applying on pointerdown, then the
 * click on a × - undid the first (review 30.10.2026). Writes chain on this one
 * until the router has caught up: `base` is the committed URL it was made
 * from; once the committed URL moves on, it is stale. Shared by every
 * component on the page, hence module level.
 */
type InFlight = { base: string; next: string; at: number };
let inFlight: InFlight | null = null;
const IN_FLIGHT_MS = 15_000;

/**
 * The URL a write builds on: the one in flight while the router has not
 * committed it (same base, not stale), else the committed one.
 */
export const searchToBuildOn = (pending: InFlight | null, committed: string, now: number): string =>
  pending && pending.base === committed && now - pending.at < IN_FLIGHT_MS ? pending.next : committed;

/**
 * The Clicks filters as the URL holds them - `filters` (what the server
 * filters on) and `where` (column conditions the grid applies to its rows) -
 * and one writer for both. The bar, the table's buttons, the menus and the
 * builder all go through it, so a change of both halves is one navigation.
 */
export function useUrlFilters() {
  const [searchParams, setSearchParams] = useSearchParams();
  const committed = searchParams.toString();
  const filtersParam = searchParams.get("filters");
  const whereParam = searchParams.get("where");
  const filters = useMemo(() => parseUrlActiveFilters(filtersParam), [filtersParam]);
  const where = useMemo(() => parseColumnFilters(whereParam), [whereParam]);

  useEffect(() => {
    if (inFlight && inFlight.base !== committed) inFlight = null;
  }, [committed]);

  /** The URL as it will be: the write in flight, else the committed one. */
  const latestSearch = useCallback((): string => searchToBuildOn(inFlight, committed, Date.now()), [committed]);

  /** Filters and conditions as they will be - for reads that must not lag a write in flight. */
  const latest = useCallback(() => {
    const p = new URLSearchParams(latestSearch());
    return { filters: parseUrlActiveFilters(p.get("filters")), where: parseColumnFilters(p.get("where")) };
  }, [latestSearch]);

  const update = useCallback(
    (change: { filters?: (cur: UrlActiveFilters) => UrlActiveFilters; where?: (cur: ColumnFilters) => ColumnFilters }) =>
      setSearchParams(() => {
        const prev = new URLSearchParams(latestSearch());
        if (change.filters) {
          const s = serializeUrlActiveFilters(change.filters(parseUrlActiveFilters(prev.get("filters"))));
          if (s) prev.set("filters", s);
          else prev.delete("filters");
        }
        if (change.where) {
          const s = serializeColumnFilters(change.where(parseColumnFilters(prev.get("where"))));
          if (s) prev.set("where", s);
          else prev.delete("where");
        }
        // A narrower result has fewer pages; staying on page 7 of 2 shows
        // nothing and reads as "the filter broke".
        prev.set("page", "1");
        inFlight = { base: committed, next: prev.toString(), at: Date.now() };
        return prev;
      }),
    [setSearchParams, latestSearch, committed],
  );

  return { filters, filtersParam, where, whereParam, latest, update };
}

/**
 * "Filter by this value" / "Exclude this value" / "Add to filter" on a table
 * value: the right-click menu and the buttons in the cell share this one.
 * Exclude adds to what is already excluded (or takes the value out of an "is"
 * list) - it used to replace the field's filter, so excluding junk rows one by
 * one brought the earlier ones back (review 30.10.2026).
 */
export function useCellFilter() {
  const { latest, update } = useUrlFilters();
  return useCallback(
    (key: string, value: string, how: "include" | "exclude" | "add", columnLabel: string) => {
      const cur = latest().filters;
      const curMode = fieldFilterOf(cur, key)?.mode;
      // A "contains" holds pieces of text, not values: adding or excluding one
      // value there has no meaning (filter-model) - say where to change it.
      const textFilter = (how === "add" || how === "exclude") && curMode !== undefined && isTextMode(curMode);
      if (textFilter) {
        toast.error(`${columnLabel} has a “${modeWord(curMode)}” filter`, {
          description: "Change it in its chip, or use Filter by this value to replace it",
        });
        return;
      }
      if (how === "add" && !addValueToFilter(cur, key, value)) {
        toast.error(`A filter holds at most ${MAX_FILTER_VALUES} values`, { description: columnLabel });
        return;
      }
      if (how === "exclude" && !excludeValues(cur, key, [value])) {
        if (curMode === "include")
          toast.error(`Nothing would be left of the ${columnLabel} filter`, {
            description: "Remove the filter instead, or exclude from a wider one",
          });
        else toast.error(`A filter holds at most ${MAX_FILTER_VALUES} values`, { description: columnLabel });
        return;
      }
      update({
        filters: (f) =>
          how === "add"
            ? (addValueToFilter(f, key, value) ?? f)
            : how === "exclude"
              ? (excludeValues(f, key, [value]) ?? f)
              : filterByValue(f, key, value, "include"),
      });
      toast.success(
        how === "include" ? `Filtered to ${value}` : how === "exclude" ? `Excluded ${value}` : `Added ${value} to filter`,
        { description: columnLabel },
      );
    },
    [latest, update],
  );
}
